import { useContext, useState, useEffect, useRef } from 'react';
import { WebApiContext } from 'ascendon-web-api';
import { translateFault, generateUnhandledFault } from '../../helpers/faults';
import { noSubscriptions } from '../../constants/subscription.constant';
import { readSessionId } from '../../utils/common';

function useSearchSubscription() {
  const [fault, setFault] = useState(null);
  const [uiModel, setUiModel] = useState(null);
  const WebApi = useContext(WebApiContext);

  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const getHeaders = ({ apiKey, headersToRemove }) => {
    const generalRequestHeaders = {
      name: 'Content-Type',
      value: 'application/json; charset=utf-8',
    };
    const setApiKey = apiKey?.length
      ? {
          add: [
            { name: 'x-api-key', value: apiKey },
            {
              name: 'Authorization',
              value: `Bearer ${readSessionId()}`,
            },
            { ...generalRequestHeaders },
          ],
        }
      : {
          add: [{ ...generalRequestHeaders }],
        };
    const headers = {
      ...setApiKey,
      remove: headersToRemove,
    };
    return headers;
  };

  const getSearchSubscriptionResponse = (responseList) =>
    (responseList || []).reduce((all, item) => {
      all.push({
        Id: item.Id,
        Product: {
          Id: item?.Items?.[0]?.Product?.Id,
          Name: item?.Items?.[0]?.Product?.Name,
          References: (
            item?.Items?.[0]?.Product?.ExternalReferences || []
          ).reduce((acc, referenceItem) => {
            acc.push({
              Type: referenceItem.ExternalId,
              Value: referenceItem.Value,
            });
            return acc;
          }, []),
        },
        PricingPlan: {
          Id: item?.Items?.[0]?.PricingPlan?.Id,
          Name: item?.Items?.[0]?.PricingPlan?.SubscriptionBillingCycleName,
          References: [],
        },
        Status: item.Status,
      });
      return all;
    }, []);

  const requestSearchSubscription = ({ emulateApigeeCall }) => {
    setUiModel(null);
    const isProduction =
      ASCENDON_ENV_CONFIG?.environment?.toLowerCase().indexOf('prod') === 1;

    const isSearchSubscriptionMicroServiceDisabled = false;
    const isEmulateApigeeCallAndNonProd =
      isSearchSubscriptionMicroServiceDisabled ||
      (emulateApigeeCall && !isProduction);

    const headers = getHeaders({
      apiKey: ASCENDON_ENV_CONFIG.contentApiKey,
      headersToRemove: [
        'CD-DeviceType',
        'CD-DistributionChannel',
        'CD-SystemId',
        'CD-SessionId',
        'CD-Language',
      ],
    });

    const getApiBasedOnEmulateApigeeCall = (isEmulateApigeeCallAndNonProd) => {
      if (isEmulateApigeeCallAndNonProd) {
        return WebApi.subscriber({
          callName: 'SearchSubscriptions',
          keyFactors: ['SearchSubscriptions'],
          forceRefresh: true,
          payload: { SortDirection: 2 },
        });
      }
      return WebApi.subscriber({
        forceRefresh: true,
        method: 'GET',
        customUrl: `${ASCENDON_ENV_CONFIG.contentApiUrl}/subscriptions`,
        headers,
      });
    };

    getApiBasedOnEmulateApigeeCall(isEmulateApigeeCallAndNonProd)
      .then((response) => {
        if (isMounted.current) {
          const searchSubscriptions = response.data;
          const getSubscriptionBaseList = () => {
            if (isEmulateApigeeCallAndNonProd) {
              return searchSubscriptions?.Subscriptions || [];
            }
            return searchSubscriptions || [];
          };

          const subscriptions = getSubscriptionBaseList();

          if (!subscriptions.length) {
            setUiModel(noSubscriptions);
            return;
          }

          const getSubscriptionResponse = (responseList) => {
            if (isEmulateApigeeCallAndNonProd) {
              return getSearchSubscriptionResponse(responseList);
            }
            return responseList;
          };
          const details = getSubscriptionResponse(subscriptions);
          setUiModel({
            details,
          });
        }
      })
      .catch((error) => {
        console.warn({ error });
        if (isMounted.current) {
          const code = error?.data?.Code;
          let errorMsg;
          if (!code) {
            const unknownFault = generateUnhandledFault(error.data);
            errorMsg = translateFault(unknownFault).translatedMessage;
            setFault(errorMsg);
          } else {
            errorMsg = translateFault(error.data).translatedMessage;
            setFault(errorMsg);
          }
        }
      });
  };
  return [uiModel, fault, requestSearchSubscription];
}

export { useSearchSubscription };
export default useSearchSubscription;
